import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Document } from 'assets/images/ic_document.svg';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import UploadModal from '../components/UploadModal';
import { TEVVService } from 'services/TEVVService';
import { Utils } from 'shared/helpers';
import moment from 'moment';
import { downloadFileSaveAs } from 'shared/helpers/DirectDownload';
import EmptyDocument from './EmptyDoment';
import { DocumentProps, TemplateProps } from '../Main';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

const BackupDocument = (props: any): ReactElement => {
  const {
    documentList,
    getDocumentList,
    templateList,
    downloadFile,
  }: {
    templateList: TemplateProps[];
    documentList: DocumentProps[];
    getDocumentList: () => void;
    downloadFile: (templateId: string, fileId: string) => void;
  } = props;
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedTemplate, setSelectedTempalte] =
    useState<DocumentProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCloseCloseDocument = (): void => {
    setShowUpload(false);
  };

  const actionOnDownload = (file: DocumentProps): void => {
    const { templateId, fileDetails } = file;
    downloadFileSaveAs(fileDetails.fileUrl, fileDetails.originalFilename);
    downloadFile(templateId, fileDetails.fileId);
  };

  const handleCloseDelete = (): void => {
    setShowDeleteModal(false);
  };
  const handleDeleteAISystem = (): void => {
    if (!selectedTemplate) return;
    const { templateId, fileDetails } = selectedTemplate;
    const { _id } = fileDetails;
    TEVVService.deleteFiles(templateId, _id)
      .then((res) => {
        getDocumentList();
        Utils.handleSuccessResponse(res);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });

    setShowDeleteModal(false);
  };

  useEffect(() => {
    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <PageHeader
        title="Backup and Redundancy Designs"
        description="Documents architecture diagrams,alternate models, and business countinuity plans."
      />
      <PageHeader
        title="Backup and Redundancy Document"
        description={`${documentList.length} Documents available`}
        actions={
          <Stack spacing={2} direction="row">
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.aiSystem.aiInventory.view.systemFailure.bypassAndRedundancy.uploadDocument'
            ) && (
              <AppPrimaryButton
                className="btn-small "
                title="Upload Document"
                onClick={() => {
                  setShowUpload(true);
                }}
                startIcon={
                  <SvgIcon
                    component={Upload}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
              />
            )}
          </Stack>
        }
      />

      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && documentList.length === 0 && (
        <EmptyDocument
          setShowUpload={(e: any) => {
            setShowUpload(e);
          }}
          showButton={orgACLConfig.aiInventory?.includes(
            'aiInventory.aiSystem.aiInventory.view.systemFailure.bypassAndRedundancy.uploadFirstDocument'
          )}
          title="No Backup and Redundancy document yet"
          subTitle=" Get started by uploading your first Backup and Redundancy Document."
        />
      )}
      {!isLoading && documentList.length > 0 && (
        <Grid container mt={4} spacing={3}>
          {documentList.map((document) => (
            <Grid item xs={4} spacing={4}>
              <Card
                className="p-4"
                sx={{ flex: 1, height: '100%', boxSizing: 'border-box' }}
              >
                <Box className="justify-space">
                  <Box className="align-center">
                    <SvgIcon
                      component={Document}
                      inheritViewBox
                      className="view-icon"
                      fontSize="small"
                      sx={{ color: 'text.primary' }}
                    />

                    <Typography className="mx-2 f16 fw-5 word-break">
                      {document.fileDetails.originalFilename}
                    </Typography>
                  </Box>
                  <Box className="align-center">
                    {orgACLConfig.aiInventory?.includes(
                      'aiInventory.aiSystem.aiInventory.view.systemFailure.bypassAndRedundancy.dowloadDocument'
                    ) && (
                      <IconButton
                        onClick={() => {
                          actionOnDownload(document);
                        }}
                      >
                        <DownloadIcon
                          fontSize="small"
                          sx={{ color: 'text.primary' }}
                        />
                      </IconButton>
                    )}
                    {orgACLConfig.aiInventory?.includes(
                      'aiInventory.aiSystem.aiInventory.view.systemFailure.bypassAndRedundancy.deleteDocument'
                    ) && (
                      <IconButton
                        onClick={() => {
                          setSelectedTempalte(document);
                          setShowDeleteModal(true);
                        }}
                      >
                        <DeleteIcon
                          fontSize="small"
                          sx={{ color: 'text.primary' }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Stack
                  direction="row"
                  className="my-2"
                  sx={{ flexWrap: 'wrap', rowGap: '10px' }}
                >
                  <Chip
                    label={document.stage}
                    sx={{
                      background: 'text.secondary',
                      color: 'text.primary',
                      border: '1px solid black',
                      marginRight: '5px',
                    }}
                    size="small"
                  />
                  {document.category && (
                    <Chip
                      label={document.category}
                      sx={{
                        background: 'white',
                        color: 'text.primary',
                        border: '1px solid black',
                      }}
                      size="small"
                    />
                  )}
                </Stack>
                <Box className="justify-space mt-3">
                  <Box className="align-center">
                    <CalendarMonthIcon fontSize="small" />
                    <Typography variant="body2">
                      {moment(document.fileDetails.createdAt).format(
                        'MMM DD YYYY'
                      )}
                    </Typography>
                  </Box>
                  {/* <Box className="flex">
                      <Typography variant="body2" className="mx-2">
                        v1.20
                      </Typography>
                      <Typography variant="body2">0.50mb</Typography>
                    </Box> */}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <ConfirmDialog
        title="Delete Backup Document"
        subTitle="Are you sure to delete the document?"
        showConfirm={showDeleteModal}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleDeleteAISystem();
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />
      <UploadModal
        title="Upload Backup Document"
        subtitle="System Failure"
        openDocumentModal={showUpload}
        cancelTitle="Cancel"
        successTitle="Upload"
        templateList={templateList}
        onSuccess={() => {
          handleCloseCloseDocument();
          // handleDeleteAISystem(aiSystemId);
        }}
        onCancel={handleCloseCloseDocument}
      />
    </>
  );
};

export default BackupDocument;
